import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Collaborator, CollaboratorRoleEnum } from "@models/collaborator";
import { LastSalesTableComponent } from "@shared/components/last-sales-table/last-sales-table.component";
import {
  OrderDetailsDialogComponent,
  OrderDetailsDialogData,
} from "@shared/modals/order-details-dialog/order-details-dialog.component";
import { PaginatorState } from "primeng/paginator";
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
} from "rxjs";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { LoadingComponent } from "../../../shared/components/loading/loading.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { EventsManagementService } from "../../services/events-management.service";
import { OrdersService } from "../../services/orders.service";
import { SalesFilters, SalesService } from "../../services/sales.service";

interface SellerFilterOption {
  id: number;
  label: string;
}

interface MySalesQuery {
  filters: SalesFilters;
  page: PaginatorState;
}

@Component({
  selector: "app-my-sales-page",
  imports: [
    CommonModule,
    FormsModule,
    PageHeaderComponent,
    AppLayoutComponent,
    LastSalesTableComponent,
    LoadingComponent,
    ButtonComponent,
  ],
  templateUrl: "./my-sales-page.component.html",
  styleUrl: "./my-sales-page.component.scss",
})
export class MySalesPageComponent implements OnInit {
  private readonly eventsManagementService = inject(EventsManagementService);
  private readonly salesService = inject(SalesService);
  private readonly ordersService = inject(OrdersService);
  private readonly dialog = inject(MatDialog);
  private readonly query$ = new BehaviorSubject<MySalesQuery>({
    filters: {},
    page: {
      first: 0,
      page: 0,
      rows: 5,
    },
  });

  sellers: SellerFilterOption[] = [];
  readonly statusOptions: { value: string; label: string }[] = [
    { value: "paid", label: "Pago" },
    { value: "receivable", label: "A receber" },
    { value: "complimentary", label: "Cortesia" },
    { value: "refunded", label: "Reembolsado" },
    { value: "cancelled", label: "Cancelado" },
    { value: "unpaid", label: "Não pago" },
  ];
  filterForm: SalesFilters = {
    seller_id: undefined,
    event_name: "",
    name: "",
    status: "",
  };
  paginatorFirst = 0;
  paginatorRows = 5;
  loadingOrderDetails = false;

  sales$ = this.query$.pipe(
    distinctUntilChanged(
      (previous, current) =>
        previous.page.page === current.page.page &&
        previous.page.rows === current.page.rows &&
        JSON.stringify(previous.filters) === JSON.stringify(current.filters),
    ),
    switchMap(({ page, filters }) => this.salesService.getSales(page, filters)),
  );

  get hasActiveFilters(): boolean {
    const filters = this.query$.value.filters;
    return (
      filters.seller_id !== undefined ||
      !!filters.event_name ||
      !!filters.name ||
      !!filters.status
    );
  }

  ngOnInit(): void {
    this.loadSellerOptions();
  }

  handleSalesPageChange(page: PaginatorState) {
    this.paginatorFirst = page.first ?? 0;
    this.paginatorRows = page.rows ?? this.paginatorRows;
    this.query$.next({
      ...this.query$.value,
      page,
    });
  }

  handleSaleDetailsClick(orderId: number) {
    this.loadingOrderDetails = true;
    this.ordersService
      .getOrderById(orderId)
      .pipe(finalize(() => (this.loadingOrderDetails = false)))
      .subscribe((order) => {
        this.dialog.open(OrderDetailsDialogComponent, {
          width: "800px",
          maxWidth: "95vw",
          data: { order } as OrderDetailsDialogData,
        });
      });
  }

  applyFilters(): void {
    this.paginatorFirst = 0;
    this.query$.next({
      filters: this.buildFilters(),
      page: {
        ...this.query$.value.page,
        first: 0,
        page: 0,
      },
    });
  }

  clearFilters(): void {
    this.filterForm = {
      seller_id: undefined,
      event_name: "",
      name: "",
      status: "",
    };
    this.applyFilters();
  }

  private buildFilters(): SalesFilters {
    const filters: SalesFilters = {};

    if (this.filterForm.seller_id !== undefined) {
      filters.seller_id = this.filterForm.seller_id;
    }
    if (this.filterForm.event_name?.trim()) {
      filters.event_name = this.filterForm.event_name.trim();
    }
    if (this.filterForm.name?.trim()) {
      filters.name = this.filterForm.name.trim();
    }
    if (this.filterForm.status) {
      filters.status = this.filterForm.status;
    }

    return filters;
  }

  private loadSellerOptions(): void {
    this.eventsManagementService
      .getMyEvents(0, 250)
      .pipe(
        switchMap((eventsPage) => {
          const eventIds = eventsPage.data
            .map((event) => Number(event.id))
            .filter((eventId) => Number.isFinite(eventId));

          if (eventIds.length === 0) {
            return of([]);
          }

          return forkJoin(
            eventIds.map((eventId) =>
              this.eventsManagementService.getCollaborators(String(eventId), {
                page: 0,
                rows: 250,
              }),
            ),
          ).pipe(
            map((pages) => pages.flatMap((page) => page.data)),
            map((collaborators) => this.toSellerOptions(collaborators)),
          );
        }),
        catchError(() => of([])),
      )
      .subscribe((sellers) => {
        this.sellers = sellers;
      });
  }

  private toSellerOptions(collaborators: Collaborator[]): SellerFilterOption[] {
    const byUserId = new Map<number, SellerFilterOption>();

    for (const collaborator of collaborators) {
      if (
        collaborator.role !== CollaboratorRoleEnum.SELLER ||
        !Number.isFinite(collaborator.user_id)
      ) {
        continue;
      }

      if (!byUserId.has(collaborator.user_id)) {
        byUserId.set(collaborator.user_id, {
          id: collaborator.user_id,
          label: this.formatSellerLabel(collaborator),
        });
      }
    }

    return [...byUserId.values()].sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR"),
    );
  }

  private formatSellerLabel(collaborator: Collaborator): string {
    const fullName = `${collaborator.name ?? ""} ${
      collaborator.last_name ?? ""
    }`.trim();

    return fullName || collaborator.email;
  }
}
